import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import type { PslfGroup } from 'tio-data/data/resources/pslf-group.type';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type FeaturesService from 'tio-common/services/features';
import type PslfDebugService from 'tio-employee/services/pslf-debug';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import { getLoanSummaryRequest } from 'tio-data/requests/user';
import { findRecord, query } from '@warp-drive/utilities/json-api';

export default class AuthenticatedPslfDashboardIndexRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare pslfDebug: PslfDebugService;

  async model() {
    const userId = this.sessionContext.user.id;
    const personId = this.sessionContext.user.person.id;
    const parentModel = this.modelFor('authenticated.pslf') as {
      pslfProfile: PslfProfileModel;
      groups: PslfGroup[];
    };

    const pslfProfile = parentModel.pslfProfile;

    const [loanSummaryResp, personResp, legacyPslfRecResp, pslf0726RecResp, pslfFormsResp] =
      await Promise.all([
        userId
          ? this.store.request(getLoanSummaryRequest(userId))
          : Promise.resolve({ content: undefined as unknown }),
        this.store.request(
          findRecord<PersonModel>('person', personId!, {
            include: 'accounts.financial-institution,accounts.loans',
            reload: true,
          })
        ) as Promise<{ content: { data: PersonModel } }>,
        this.store.request(
          query<RecommendationModel>('recommendation', {
            'filter[user]': userId,
            'filter[requesting-app]': 'PSLF',
            include: 'user',
            sort: '-created-at',
            'page[offset]': 0,
            'page[limit]': 10,
          })
        ) as Promise<{ content: { data: RecommendationModel[] } }>,
        // PSLF-0726 pilot runs — surfaced (with the legacy ones) in the history
        // table when the dashboard is in ?estimator0726=true mode.
        this.store.request(
          query<RecommendationModel>(
            'recommendation',
            {
              'filter[user]': userId,
              'filter[requesting-app]': 'PSLF-0726',
              include: 'user',
              sort: '-created-at',
              'page[offset]': 0,
              'page[limit]': 10,
            },
            // Force a fresh fetch: the estimator's didSave transitions back here
            // right after creating a recommendation, and the default CachePolicy
            // would otherwise serve a stale cached result (missing the new run).
            { reload: true }
          )
        ) as Promise<{ content: { data: RecommendationModel[] } }>,
        this.store.request(
          query<PslfFormModel>('pslf-form', {
            'filter[pslf-profile]': pslfProfile.id,
            include: 'attachments',
          })
        ) as Promise<{ content: { data: PslfFormModel[] } }>,
      ]);

    const person = personResp.content.data;
    const legacyPslfRecs = legacyPslfRecResp.content.data ?? [];
    const pslf0726Recs = pslf0726RecResp.content.data ?? [];
    const pslfForms = pslfFormsResp.content.data;

    // Combined history (PSLF-0726 + legacy PSLF), newest first — mirrors the IDR
    // dashboard. Only rendered in ?estimator0726=true mode.
    const history = [...pslf0726Recs, ...legacyPslfRecs]
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
      .slice(0, 10);

    const baseModel = {
      accounts: person.accounts,
      loanSummary: loanSummaryResp.content as LoanSummary,
      // Unchanged hero behavior: the legacy "View Estimate" uses the most recent
      // legacy PSLF recommendation.
      mostRecentRecommendation: legacyPslfRecs[0] as RecommendationModel | undefined,
      history,
      person: person,
      pslfProfile: pslfProfile,
      pslfForms: pslfForms.slice() as PslfFormModel[],
      debugLinkLoans: false,
    };

    // Apply PSLF debug overrides (feature-flagged; dead code in production)
    if (this.features.isEnabled('pslfDebugPanel') && this.pslfDebug.isActive()) {
      return this.pslfDebug.applyTo(baseModel);
    }

    return baseModel;
  }
}
